import type { Breadcrumb, ErrorEvent, SpanAttributeValue, SpanJSON, TransactionEvent } from "@sentry/core";
import { withStaticSpan } from "@sentry/react";
import type { BrowserOptions } from "@sentry/react";
import { SENTRY_DATA_COLLECTION } from "@shared/sentry-data-collection";

type BrowserEnvLike = {
  MODE?: string;
  PROD?: boolean;
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
};

const DEFAULT_TRACES_SAMPLE_RATE = 0.1;
const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>]+/g;

export function shouldInitializeBrowserSentry(env: BrowserEnvLike): boolean {
  return env.PROD === true && !!env.VITE_SENTRY_DSN;
}

function parseSampleRate(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.min(1, parsed));
}

function redactUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return url;
  }
}

function redactUrlsInText(value: string): string {
  return value.replace(URL_IN_TEXT_PATTERN, match => redactUrl(match));
}

function redactUnknownUrlLikeValue(value: unknown): unknown {
  return typeof value === "string" ? redactUrlsInText(value) : value;
}

function redactSpanAttributeValue(value: SpanAttributeValue | undefined): SpanAttributeValue | undefined {
  return typeof value === "string" ? redactUrlsInText(value) : value;
}

export function sanitizeBrowserSentryEvent(event: ErrorEvent): ErrorEvent {
  const url = event.request?.url;
  const sanitizedMessage = typeof event.message === "string" ? redactUrlsInText(event.message) : event.message;

  if (!url) return sanitizedMessage === event.message ? event : { ...event, message: sanitizedMessage };

  return {
    ...event,
    message: sanitizedMessage,
    request: {
      ...event.request,
      url: redactUrl(url),
    },
  };
}

export function sanitizeBrowserBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  return {
    ...breadcrumb,
    message: typeof breadcrumb.message === "string" ? redactUrlsInText(breadcrumb.message) : breadcrumb.message,
    data: breadcrumb.data
      ? Object.fromEntries(Object.entries(breadcrumb.data).map(([key, value]) => [key, redactUnknownUrlLikeValue(value)]))
      : breadcrumb.data,
  };
}

export function sanitizeBrowserSentrySpan(span: SpanJSON): SpanJSON {
  const data: Record<string, SpanAttributeValue | undefined> = {};
  for (const [key, value] of Object.entries(span.data)) {
    data[key] = redactSpanAttributeValue(value);
  }

  return {
    ...span,
    description: typeof span.description === "string" ? redactUrlsInText(span.description) : span.description,
    data,
  };
}

export function sanitizeBrowserSentryTransaction(event: TransactionEvent): TransactionEvent {
  return {
    ...event,
    request: event.request?.url
      ? {
          ...event.request,
          url: redactUrl(event.request.url),
        }
      : event.request,
    transaction: typeof event.transaction === "string" ? redactUrlsInText(event.transaction) : event.transaction,
    spans: event.spans?.map(sanitizeBrowserSentrySpan),
  };
}

export function buildBrowserSentryOptions(env: BrowserEnvLike): BrowserOptions {
  return {
    dsn: env.VITE_SENTRY_DSN,
    environment: env.MODE || "production",
    tracesSampleRate: parseSampleRate(env.VITE_SENTRY_TRACES_SAMPLE_RATE, DEFAULT_TRACES_SAMPLE_RATE),
    // HUB-619: Sentry 11 streams spans by default. In that mode beforeSendTransaction never runs and
    // an unwrapped static span callback is ignored, so URLs would reach Sentry unredacted. Keep the
    // transaction-based lifecycle and mark the span sanitizer as static-format.
    traceLifecycle: "static",
    // HUB-619: keep v10's data-collection and stack-trace defaults (v11 widened both).
    dataCollection: SENTRY_DATA_COLLECTION,
    attachStacktrace: false,
    beforeSend: sanitizeBrowserSentryEvent,
    beforeBreadcrumb: sanitizeBrowserBreadcrumb,
    beforeSendSpan: withStaticSpan(sanitizeBrowserSentrySpan),
    beforeSendTransaction: sanitizeBrowserSentryTransaction,
  };
}
